/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    bool, func, object, string,
} from 'prop-types';
import { getFeatureFlags, getEnterpriseCalendarSFABTest } from '../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import CalendarVariation from '../CalendarVariation/CalendarVariation';
import EnterpriseCalendarDialog from './EnterpriseCalendarDialog';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useClickStreamCustomEventsTracking from '../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';

const CalendarBuilder = ({
    brandPresentationFamily,
    contextUserState,
    deliveryDate,
    deliveryTextData,
    handleDeliveryCalendarClose,
    itemRecipientData,
    ldClient,
    openCalendar,
    orderDetails,
    pageProcess,
    productBrandId,
    zipCode,
}) => {
    const dispatchAction = useDispatch();

    const featureFlags = useSelector(getFeatureFlags);
    const enterpriseCalendarSFABTestData = useSelector(getEnterpriseCalendarSFABTest);

    const isEnterpriseCalendarEnabled = featureFlags['is-enterprise-calendar-enabled'] === 'newflow'
        || (
            featureFlags['is-enterprise-calendar-enabled'] === 'salesforce'
            && enterpriseCalendarSFABTestData?.campaign?.campaignResponses?.[0]?.userGroup === 'Default'
        );

    const isEnterpriseCalendarProductEnabled = isEnterpriseCalendarEnabled
        && featureFlags['which-brand-allow-enterprise-calendar-enabled']?.allow?.includes(orderDetails.product?.brandCode);

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: enterpriseCalendarSFABTestData || null,
        isFireImpression: false,
        page: { type: pageProcess },
    });

    useEffect(() => {
        dispatchAction(trackEvent({
            eventName: 'impression',
            eventCategory: 'Experiment',
            eventAction: 'Enterprise Calendar',
            eventLabel: isEnterpriseCalendarProductEnabled ? 'Variation' : 'Control',
        }));

        if (
            featureFlags['is-enterprise-calendar-enabled'] === 'salesforce'
            && enterpriseCalendarSFABTestData?.campaign?.campaignResponses?.[0]?.userGroup === 'Default'
        ) {
            trackClickThrough();
        }
    }, []);

    if (isEnterpriseCalendarProductEnabled) {
        return (
            <EnterpriseCalendarDialog
                recipientItem={itemRecipientData}
                orderItem={orderDetails}
                openCalendar={openCalendar}
                handleDeliveryCalendarClose={handleDeliveryCalendarClose}
                pageType={pageProcess}
            />
        );
    }

    return (
        <CalendarVariation
            openCalendar={openCalendar}
            brandPresentationFamily={brandPresentationFamily}
            deliveryDate={deliveryDate}
            deliveryTextData={deliveryTextData}
            orderDetails={orderDetails}
            zipCode={zipCode}
            handleDeliveryCalendarClose={handleDeliveryCalendarClose}
            pageProcess={pageProcess}
            contextUserState={contextUserState}
            itemRecipientData={itemRecipientData}
            ldClient={ldClient}
            productBrandId={productBrandId}
        />
    );
};

CalendarBuilder.propTypes = {
    brandPresentationFamily: string.isRequired,
    contextUserState: string.isRequired,
    deliveryDate: string.isRequired,
    deliveryTextData: object.isRequired,
    handleDeliveryCalendarClose: func.isRequired,
    itemRecipientData: object.isRequired,
    ldClient: object,
    openCalendar: bool.isRequired,
    orderDetails: object.isRequired,
    pageProcess: string.isRequired,
    productBrandId: string.isRequired,
    zipCode: string.isRequired,
};

CalendarBuilder.defaultProps = {
    ldClient: null,
};

export default CalendarBuilder;
